import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CircleAlert } from "lucide-react";
import { Spinner } from "@/components/spinner";
import { creditExhausted, daysLeft, formatDay, formatEur, spentShare } from "@/lib/credit";
import { tools } from "@/lib/tools";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authed/einstellungen/nutzung")({
  component: Usage,
});

/**
 * The credit in euros and its pace, nothing about models or tokens: a
 * practice decides by what is left and how long it lasts.
 */
function Usage() {
  const me = useQuery(tools.queryOptions("GET /api/me", {}));
  const usage = useQuery(tools.queryOptions("GET /api/me/usage", {}));

  const error = me.error ?? usage.error;
  if (error) return <p role="alert">{error.message}</p>;
  if (!me.data || !usage.data) return <Spinner />;

  const { credit } = me.data;
  const { days } = usage.data;
  const exhausted = creditExhausted(credit);
  const left = daysLeft(credit.remaining, days);

  return (
    <div className="space-y-6">
      {exhausted ? (
        <div
          role="alert"
          className="flex gap-3 rounded-xl border border-destructive bg-destructive/5 p-4 text-sm"
        >
          <CircleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-destructive" />
          <div className="space-y-1">
            <p className="font-medium">Ihr Guthaben ist aufgebraucht.</p>
            <p className="text-muted-foreground">
              Neue Anfragen sind pausiert. Ihre Unterhaltungen bleiben lesbar.
            </p>
          </div>
        </div>
      ) : null}

      <section aria-labelledby="guthaben" className="space-y-4 rounded-xl border p-6">
        <h2
          id="guthaben"
          className="font-medium text-[11px] text-muted-foreground uppercase tracking-wider"
        >
          Guthaben
        </h2>
        <p className="flex flex-wrap items-baseline gap-x-2">
          <span
            data-testid="credit"
            className={cn(
              "font-semibold text-4xl tabular-nums tracking-tight",
              exhausted && "text-destructive",
            )}
          >
            {formatEur(credit.remaining)}
          </span>
          <span className="text-muted-foreground text-sm">
            verbleibend von {formatEur(credit.credit)}
          </span>
        </p>
        {/* The amounts around it say what the bar shows. */}
        <div aria-hidden className="h-2 overflow-hidden rounded-full bg-muted">
          <div
            className={cn("h-full rounded-full", exhausted ? "bg-destructive" : "bg-primary")}
            style={{ width: `${spentShare(credit) * 100}%` }}
          />
        </div>
        <p className="text-muted-foreground text-sm">
          {formatEur(credit.spent)} verbraucht
          {!exhausted && left !== undefined
            ? ` · reicht beim Verbrauch der letzten 30 Tage etwa ${left} ${left === 1 ? "Tag" : "Tage"}`
            : null}
        </p>
      </section>

      <section aria-labelledby="verlauf" className="space-y-4 rounded-xl border p-6">
        <h2 id="verlauf" className="font-semibold">
          Verbrauch pro Tag
        </h2>
        <DailySpend days={days} />
      </section>
    </div>
  );
}

function DailySpend({ days }: { days: readonly { date: string; spent: number }[] }) {
  const max = Math.max(...days.map((day) => day.spent));
  const first = days[0];
  if (max <= 0 || !first)
    return (
      <p className="text-muted-foreground text-sm">In den letzten 30 Tagen nichts verbraucht.</p>
    );
  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-1">
      <div
        aria-hidden
        className="flex h-32 flex-col justify-between text-right text-[11px] text-muted-foreground tabular-nums"
      >
        <span>{formatEur(max)}</span>
        <span>0 €</span>
      </div>
      {/* The bars are a picture of the list a screen reader gets instead. */}
      <div aria-hidden className="flex h-32 items-end gap-0.5 border-b">
        {days.map((day) => (
          <div
            key={day.date}
            title={`${formatDay(day.date)}: ${formatEur(day.spent)}`}
            className={cn("flex-1 rounded-t-sm", day.spent > 0 ? "bg-accent" : "bg-border")}
            style={{ height: `${Math.max(2, (day.spent / max) * 100)}%` }}
          />
        ))}
      </div>
      <ul className="sr-only">
        {days
          .filter((day) => day.spent > 0)
          .map((day) => (
            <li key={day.date}>
              {formatDay(day.date)}: {formatEur(day.spent)}
            </li>
          ))}
      </ul>
      <div
        aria-hidden
        className="col-start-2 flex justify-between text-[11px] text-muted-foreground"
      >
        <span>{formatDay(first.date)}</span>
        <span>heute</span>
      </div>
    </div>
  );
}
